import api from './api';
import { message } from 'antd';

// 媒体文件上传服务
const fileService = {
  
  /**
   * 上传临时素材（视频等）
   * @param {File} file 文件对象
   * @param {string} mediaType 媒体类型 (video)
   * @param {number} tenantId 租户ID
   * @param {string} sceneType 场景类型 (message/moment/material)
   * @param {function} onProgress 进度回调
   * @returns {Promise<{success: boolean, mediaId: string, message: string, ossUrl: string}>} 上传结果
   */
  async uploadMedia(file, mediaType, tenantId, sceneType = 'message', onProgress) {
    try {
      // 创建FormData对象
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', mediaType);
      formData.append('tenantId', tenantId);
      formData.append('sceneType', sceneType);
      
      // 调用后端API上传
      const response = await api.post('/media/wework/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Tenant-ID': tenantId
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        }
      });
      
      // 处理响应
      if (response.data.success) {
        return {
          success: true,
          mediaId: response.data.mediaId,
          ossUrl: response.data.ossUrl,
          message: '上传成功',
          previewType: response.data.previewType
        };
      } else {
        return {
          success: false,
          message: response.data.message || '上传失败'
        };
      }
    } catch (error) {
      console.error('上传临时素材失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || '上传失败'
      };
    }
  },
  
  /**
   * 上传图片（永久素材）
   * @param {File} file 文件对象
   * @param {number} tenantId 租户ID
   * @param {function} onProgress 进度回调
   * @returns {Promise<{success: boolean, url: string, message: string, ossUrl: string}>} 上传结果
   */
  async uploadImage(file, tenantId, onProgress) {
    try {
      // 创建FormData对象
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tenantId', tenantId);
      
      // 调用后端API上传
      const response = await api.post('/media/wework/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Tenant-ID': tenantId
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        }
      });
      
      // 处理响应
      if (response.data.success) {
        return {
          success: true,
          url: response.data.url,
          ossUrl: response.data.ossUrl,
          message: '上传成功',
          previewType: response.data.previewType
        };
      } else {
        return {
          success: false,
          message: response.data.message || '上传失败'
        };
      }
    } catch (error) {
      console.error('上传永久图片失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || '上传失败'
      };
    }
  },
  
  /**
   * 上传朋友圈附件（专用临时素材）
   * @param {File} file 文件对象
   * @param {string} mediaType 媒体类型 (image/video)
   * @param {number} tenantId 租户ID
   * @param {function} onProgress 进度回调
   * @returns {Promise<{success: boolean, mediaId: string, message: string}>} 上传结果
   */
  async uploadMomentAttachment(file, mediaType, tenantId, onProgress) {
    try {
      // 创建FormData对象
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', mediaType);
      formData.append('tenantId', tenantId);
      formData.append('attachmentType', 1); // 1表示朋友圈附件
      formData.append('sceneType', 'moment'); // 朋友圈使用moment场景
      
      // 调用后端API上传朋友圈附件
      const response = await api.post('/media/wework/upload_attachment', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Tenant-ID': tenantId
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        }
      });
      
      // 处理响应
      if (response.data.success) {
        return {
          success: true,
          mediaId: response.data.mediaId,
          ossUrl: response.data.ossUrl,
          message: '朋友圈附件上传成功',
          previewType: response.data.previewType
        };
      } else {
        return {
          success: false,
          message: response.data.message || '朋友圈附件上传失败'
        };
      }
    } catch (error) {
      console.error('上传朋友圈附件失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || '上传朋友圈附件失败'
      };
    }
  },
  
  /**
   * 获取媒体预览URL
   * @param {string} mediaId 媒体ID
   * @param {number} tenantId 租户ID
   * @returns {Promise<string>} 预览URL
   */
  async getMediaPreviewUrl(mediaId, tenantId) {
    // 实际上企业微信的临时媒体无法直接预览，这里返回一个空值
    // 前端可以根据媒体类型显示占位图
    return '';
  },
  
  /**
   * 获取OSS文件预览URL
   * @param {string} ossUrl OSS文件URL
   * @returns {Promise<{success: boolean, url: string, message: string}>} 预览URL结果
   */
  async getOssPreviewUrl(ossUrl) {
    try {
      if (!ossUrl) {
        return {
          success: false,
          message: 'OSS URL不能为空'
        };
      }
      
      const response = await api.get(`/media/oss/preview?url=${encodeURIComponent(ossUrl)}`);
      
      if (response.data.success) {
        return {
          success: true,
          url: response.data.url,
          message: '获取预览URL成功'
        };
      } else {
        return {
          success: false,
          message: response.data.message || '获取预览URL失败'
        };
      }
    } catch (error) {
      console.error('获取OSS预览URL失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || '获取预览URL失败'
      };
    }
  }
};

export default fileService; 